<template>
  <div class="screen">
    <h2>Play with friends</h2>

    <div v-if="roomError" class="notice notice--bad">{{ roomError }}</div>

    <!-- Not in a room yet -->
    <template v-if="!roomId">
      <!-- Your async games, across every game on the site (atrAsync, kept
           by the hub's Lambda): the ones waiting on you first. -->
      <div v-if="myGames.length" class="my-games">
        <h3 class="my-games__title">Your games</h3>
        <a v-for="g in myGames" :key="g.key" class="my-game" :class="{ 'is-my-turn': g.myTurn }" :href="g.url">
          <span class="my-game__name">{{ g.name }}<span v-if="g.code" class="my-game__code"> · {{ g.code }}</span></span>
          <span class="my-game__who">{{ g.myTurn ? 'Your turn' : (g.status === 'lobby' ? 'Not started' : g.waitingOn && g.waitingOn.length ? `Waiting on ${g.waitingOn.join(', ')}` : 'In progress') }}</span>
        </a>
      </div>

      <button class="primary" @click="host">Start a new game</button>
      <!-- Async play (2026-10-04): the room waits for people instead of
           handing their seats to bots, and the hub tells them when it's
           their turn. See src/store/asyncPlay.js. -->
      <label class="async-toggle">
        <input v-model="playAsync" type="checkbox" />
        <span>Play over days — the game waits for each person, and tells them when it's their turn</span>
      </label>
      <TurnBell :game="gamePrefix" />

      <div class="field">
        <label for="join-code">Or join with a code</label>
        <div class="join-row">
          <input
            id="join-code"
            v-model="code"
            class="code-input"
            maxlength="4"
            autocomplete="off"
            autocapitalize="characters"
            spellcheck="false"
            placeholder="FROG"
            @keyup.enter="join"
          />
          <button class="secondary join-button" :disabled="code.length < 4" @click="join">Join</button>
        </div>
      </div>

      <button class="secondary" @click="$emit('back')">Back</button>
    </template>

    <!-- In the lobby, waiting to deal -->
    <template v-else-if="isLobby">
      <div class="room-code">
        <span class="room-code__label">Room code</span>
        <strong class="room-code__value">{{ roomMeta?.roomCode }}</strong>
        <button class="secondary share-button" @click="share">{{ shareLabel }}</button>
      </div>

      <ul class="seats">
        <li v-for="slot in seats" :key="slot.uid" class="seat">
          <span class="seat__name">{{ slot.displayName }}</span>
          <span v-if="slot.uid === roomMeta.hostUid" class="seat__tag">host</span>
          <span v-if="slot.uid === myUid" class="seat__tag">you</span>
          <span class="seat__dot" :class="{ 'seat__dot--on': presence[slot.uid] }" />
        </li>
        <li v-for="n in botSeats" :key="`bot-${n}`" class="seat seat--bot">
          <span class="seat__name">{{ n }}</span>
          <span class="seat__tag">bot</span>
        </li>
      </ul>

      <div v-if="isHost" class="field">
        <label>Players at the table</label>
        <div class="segmented">
          <button
            v-for="n in tableSizes"
            :key="n"
            :class="{ 'is-selected': n === tableSize }"
            :disabled="n < seats.length"
            :title="n < seats.length ? 'More people than that are already here' : ''"
            @click="setTableSize(n)"
          >{{ n }}</button>
        </div>
      </div>

      <p class="muted">
        {{ seats.length }} {{ seats.length === 1 ? 'person' : 'people' }} here,
        table set to {{ tableSize }}.
        <template v-if="botSeats.length">
          {{ botSeats.length }} bot{{ botSeats.length === 1 ? '' : 's' }} will take the
          {{ botSeats.length === 1 ? 'last seat' : 'empty seats' }}.
        </template>
        <template v-else>Every seat is taken by a person.</template>
      </p>

      <button v-if="isHost" class="primary" @click="deal">Set off</button>
      <p v-else class="muted">Waiting for the host to set off…</p>

      <button class="secondary" @click="leave">Leave room</button>
    </template>

    <!-- A game is running and we're rejoining it -->
    <template v-else>
      <p class="muted">Rejoining game…</p>
      <button class="secondary" @click="leave">Leave</button>
    </template>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import { ref as dbRef, onValue } from 'firebase/database';
import { db } from '@/firebase';
import { ASYNC_INDEX } from '@/store/asyncPlay.js';
import { GAMES } from '@/store/multiplayer';
import TurnBell from '@/components/shared/TurnBell.vue';
import { MIN_PLAYERS, MAX_PLAYERS } from '@/engine/constants';
import { PERSONALITIES, personalityFor } from '@/engine/ai/heuristicBot';

export default {
  name: 'OnlineLobby',
  components: { TurnBell },
  emits: ['back'],
  data() {
    return {
      playAsync: false,
      myGamesRows: {}, code: '', shareLabel: 'Copy link' };
  },
  computed: {
    ...mapState(['roomId', 'roomMeta', 'presence', 'roomError']),
    gamePrefix() { return GAMES.replace(/Games$/, ''); },
    myGames() {
      return Object.entries(this.myGamesRows || {})
        .map(([key, row]) => ({ key, ...row }))
        .sort((a, b) => (b.myTurn - a.myTurn) || (b.updatedAt || 0) - (a.updatedAt || 0));
    },
    ...mapGetters(['isHost', 'myUid']),
    isLobby() { return this.roomMeta?.status === 'lobby'; },
    seats() {
      return [...(this.roomMeta?.playerSlots || [])].sort((a, b) => a.seatIndex - b.seatIndex);
    },
    tableSizes() {
      const sizes = [];
      for (let n = MIN_PLAYERS; n <= MAX_PLAYERS; n += 1) sizes.push(n);
      return sizes;
    },
    tableSize() {
      // Never shows fewer seats than there are people already sitting down -
      // mirrors the same clamp startRoom applies, so the lobby always
      // describes the table you're actually going to get.
      return Math.max(this.roomMeta?.tableSize || MIN_PLAYERS, this.seats.length, MIN_PLAYERS);
    },
    /**
     * The bots that will be dealt in - whatever seats the humans haven't
     * taken. Mirrors startRoom's arithmetic exactly.
     */
    botSeats() {
      const count = Math.max(0, this.tableSize - this.seats.length);
      return Array.from({ length: count }, (_, i) => PERSONALITIES[personalityFor(i)].label);
    },
  },
  mounted() {
    this.followMyGames();
    // Opening this screen is the natural moment to tidy up: you are already
    // thinking about rooms, and nothing here is waiting on the result.
    this.$store.dispatch('sweepRooms');
  },
  watch: {
    code(value) {
      this.code = String(value || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
    },
  },
  methods: {
    followMyGames() {
      const uid = this.$store.state.user?.uid;
      if (!uid || !db) return;
      this.offMyGames = onValue(dbRef(db, `${ASYNC_INDEX}/${uid}`), (snap) => { this.myGamesRows = snap.val() || {}; });
    },
    host() { this.$store.dispatch('hostRoom', { async: this.playAsync }); },
    setTableSize(n) { this.$store.dispatch('changeTableSize', n); },
    join() { this.$store.dispatch('joinRoomByCode', this.code); },
    deal() { this.$store.dispatch('dealRoom'); },
    leave() { this.$store.dispatch('leaveRoom'); },
    async share() {
      // A link carrying the code is far easier to send than four letters read
      // down a phone, but the letters still work for someone reading them out.
      // The invite carries the CODE as well as the link (Matt, 2026-10-04): on
      // an iPhone a link opens in Safari, never in the installed app, so a
      // friend who has the app wants the code to type into it instead.
      const code = this.roomMeta?.roomCode;
      const text = `Join me for Incan Gold${this.roomMeta?.async ? ' — we play over days, so take your turns whenever' : ''}. Code ${code}`;
      const url = `${window.location.origin}/?join=${this.roomMeta?.roomCode}`;
      try {
        if (navigator.share) {
          await navigator.share({ title: 'Incan Gold', text, url });
          return;
        }
        await navigator.clipboard.writeText(`${text} · ${url}`);
        this.shareLabel = 'Copied';
        window.setTimeout(() => { this.shareLabel = 'Copy link'; }, 1800);
      } catch {
        // Share sheet dismissed, or clipboard blocked. The code is on screen.
        this.shareLabel = 'Copy link';
      }
    },
  },
};
</script>

<style scoped>
.my-games { display: flex; flex-direction: column; gap: 0.4rem; margin-bottom: 0.5rem; }
.my-games__title { margin: 0 0 0.2rem; font-size: 0.85rem; text-transform: uppercase; letter-spacing: 0.08em; opacity: 0.7; }
.my-game { display: flex; justify-content: space-between; gap: 0.75rem; padding: 0.6rem 0.8rem; border: 1px solid currentColor; border-radius: 0.6rem; text-decoration: none; color: inherit; opacity: 0.85; }
.my-game.is-my-turn { opacity: 1; font-weight: 700; }
.my-game__code { opacity: 0.7; font-weight: 400; }
.async-toggle { display: flex; gap: 0.6rem; align-items: flex-start; font-size: 0.9rem; line-height: 1.35; margin: 0.4rem 0; }
.async-toggle input { margin-top: 0.2rem; }
</style>